import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type SVGProps,
} from 'react';
import metadata from './icon-metadata.json';
import motionPresets from './motion-presets.json';
import { toneStreamlineSvg, type ParsedStreamlineSvg } from './streamlineSvg';
import './roam-icons.css';

const svgLoaders = import.meta.glob<string>('./bundled/**/*.svg', {
  query: '?raw',
  import: 'default',
});

const svgCache = new Map<string, string>();

async function loadSvgRaw(relativePath: string) {
  const cached = svgCache.get(relativePath);
  if (cached) return cached;
  const key = `./bundled/${relativePath}`;
  const loader = svgLoaders[key];
  if (!loader) throw new Error(`Missing bundled Streamline SVG: ${relativePath}`);
  const raw = await loader();
  svgCache.set(relativePath, raw);
  return raw;
}

export type RoamIconName = keyof typeof metadata.icons;
export type RoamIconVariant = 'line' | 'solid' | 'duo' | 'flat';

export type RoamIconProps = SVGProps<SVGSVGElement> & {
  size?: number | string;
  /** Core style; defaults to Line. Solid is for selected navigation. */
  variant?: RoamIconVariant;
  /** Shorthand for `variant="solid"` when a nav item is active. */
  selected?: boolean;
};

type VariantMap = Partial<Record<RoamIconVariant, string>>;

function resolveVariant(
  name: RoamIconName,
  variant: RoamIconVariant,
  selected: boolean,
): RoamIconVariant {
  const available = metadata.icons[name]?.variants as VariantMap;
  const preferred = selected ? 'solid' : variant;
  if (available[preferred]) return preferred;
  if (available.solid && selected) return 'solid';
  if (available.line) return 'line';
  if (available[variant]) return variant;
  return 'line';
}

export const RoamIcon = forwardRef<SVGSVGElement, RoamIconProps & { name: RoamIconName }>(
  function RoamIcon(
    { name, size = 24, className, variant = 'line', selected = false, children, style, ...props },
    ref,
  ) {
    const icon = metadata.icons[name];
    const resolved = resolveVariant(name, variant, selected);
    const relativePath = (icon.variants as VariantMap)[resolved]!;
    const mono = resolved === 'line' || resolved === 'solid';
    const labelled = Boolean(props['aria-label'] || props['aria-labelledby']);
    const [parsed, setParsed] = useState<ParsedStreamlineSvg | null>(() => {
      const cached = svgCache.get(relativePath);
      return cached ? toneStreamlineSvg(cached, mono ? 'mono' : 'preserve') : null;
    });

    useLayoutEffect(() => {
      const cached = svgCache.get(relativePath);
      setParsed(cached ? toneStreamlineSvg(cached, mono ? 'mono' : 'preserve') : null);
    }, [relativePath, mono]);

    useEffect(() => {
      let cancelled = false;
      void loadSvgRaw(relativePath)
        .then((raw) => {
          if (cancelled) return;
          setParsed(toneStreamlineSvg(raw, mono ? 'mono' : 'preserve'));
        })
        .catch(() => {
          if (!cancelled) setParsed(null);
        });
      return () => {
        cancelled = true;
      };
    }, [relativePath, mono]);

    const svgStyle: CSSProperties = {
      ...style,
      color: mono ? 'var(--roam-icon-ink, #1f1c1c)' : undefined,
    };

    const viewBox = parsed?.viewBox ?? '0 0 14 14';

    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox={viewBox}
        fill="none"
        aria-hidden={labelled ? undefined : true}
        role={labelled ? 'img' : undefined}
        focusable="false"
        {...props}
        style={svgStyle}
        className={['roam-icon', name === 'loading' ? 'roam-icon--spin' : null, className]
          .filter(Boolean)
          .join(' ')}
        data-roam-icon={name}
        data-roam-icon-variant={resolved}
        data-motion={icon.motion}
      >
        {children}
        {parsed ? <g dangerouslySetInnerHTML={{ __html: parsed.inner }} /> : null}
      </svg>
    );
  },
);

export type RoamIconComponent = ReturnType<typeof createRoamIcon>;

function createRoamIcon(name: RoamIconName) {
  const Component = forwardRef<SVGSVGElement, RoamIconProps>((props, ref) => (
    <RoamIcon {...props} ref={ref} name={name} />
  ));
  Component.displayName = `RoamIcon(${name})`;
  return Component;
}

export const ROAM_ICONS = Object.fromEntries(
  (Object.keys(metadata.icons) as RoamIconName[]).map((name) => [name, createRoamIcon(name)]),
) as Record<RoamIconName, RoamIconComponent>;

/** Attach to a button/link's onPointerDown or onClick. Never animates at rest. */
export function playRoamIconMotion(event: { currentTarget: HTMLElement }) {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  for (const icon of event.currentTarget.querySelectorAll<SVGSVGElement>('.roam-icon')) {
    if (typeof icon.animate !== 'function') continue;
    const motion = icon.dataset.motion as keyof typeof motionPresets;
    const preset = motionPresets[motion] ?? motionPresets.lift;
    icon.getAnimations?.().forEach((animation) => animation.cancel());
    icon.animate(preset, { duration: 420, easing: 'ease-out' });
  }
}
